import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plus, Trash2, Mail, Save, Building2, Phone, Users } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const SETTING_KEY = 'sales_lead_config';

type LeadOrigin = 'signup' | 'licenses' | 'contact';

const ORIGIN_LABELS: Record<LeadOrigin, string> = {
  signup: 'Registro de empresa',
  licenses: 'Solicitud de licencias',
  contact: 'Formulario de contacto',
};

function leadOrigin(lookingFor: string): LeadOrigin {
  const t = (lookingFor ?? '').toLowerCase();
  if (t.startsWith('registro de empresa')) return 'signup';
  if (t.startsWith('solicitud de contratación')) return 'licenses';
  return 'contact';
}

interface SalesLeadConfig {
  recipient_emails: string[];
}

interface SalesLead {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  company_name: string;
  position: string;
  employee_count: string;
  looking_for: string;
  created_at: string;
}

export default function SalesLeadsConfigTab() {
  const qc = useQueryClient();
  const [emails, setEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [originFilter, setOriginFilter] = useState<'all' | LeadOrigin>('all');


  const { data: configData, isLoading: configLoading } = useQuery({
    queryKey: ['platform_settings', SETTING_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', SETTING_KEY)
        .maybeSingle();
      if (error) throw error;
      return (data?.value as unknown as SalesLeadConfig) ?? null;
    },
  });

  const { data: leads, isLoading: leadsLoading } = useQuery({
    queryKey: ['sales_leads'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sales_leads' as any)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return ((data ?? []) as unknown) as SalesLead[];
    },
  });

  const filteredLeads = (leads ?? []).filter(
    (l) => originFilter === 'all' || leadOrigin(l.looking_for) === originFilter,
  );


  useEffect(() => {
    if (configData) setEmails(configData.recipient_emails ?? []);
  }, [configData]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('platform_settings')
        .upsert({ key: SETTING_KEY, value: { recipient_emails: emails } as unknown as Json }, { onConflict: 'key' });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform_settings', SETTING_KEY] });
      toast({ title: 'Configuración guardada' });
    },
    onError: () => toast({ title: 'Error al guardar', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('sales_leads' as any).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales_leads'] });
      setDeleteId(null);
      toast({ title: 'Prospecto eliminado' });
    },
    onError: () => toast({ title: 'Error al eliminar', variant: 'destructive' }),
  });

  const addEmail = () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast({ title: 'Correo inválido', variant: 'destructive' });
      return;
    }
    if (emails.includes(trimmed)) {
      toast({ title: 'Este correo ya está en la lista', variant: 'destructive' });
      return;
    }
    setEmails([...emails, trimmed]);
    setNewEmail('');
  };

  if (configLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Recipient config */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Destinatarios de notificación
          </CardTitle>
          <CardDescription>
            Cada vez que alguien envíe el formulario de contacto desde la página empresarial,
            recibirán un correo los destinatarios configurados aquí.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="correo@empresa.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addEmail()}
              className="max-w-sm"
            />
            <Button variant="outline" size="sm" onClick={addEmail}>
              <Plus className="w-4 h-4 mr-1" />
              Agregar
            </Button>
          </div>

          {emails.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Sin destinatarios configurados. Los prospectos se guardarán en la plataforma pero no se enviarán correos.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {emails.map((email) => (
                <Badge key={email} variant="secondary" className="gap-1.5 pr-1.5">
                  {email}
                  <button
                    onClick={() => setEmails(emails.filter((e) => e !== email))}
                    className="hover:text-destructive transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <div className="flex justify-end pt-2 border-t border-border">
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} size="sm">
              {saveMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Leads table */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold">Prospectos recibidos</h3>
            <p className="text-sm text-muted-foreground">
              Registros de empresa, solicitudes de licencias y formularios de contacto.
            </p>
          </div>
          <Select value={originFilter} onValueChange={(v) => setOriginFilter(v as typeof originFilter)}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Origen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los orígenes</SelectItem>
              <SelectItem value="signup">{ORIGIN_LABELS.signup}</SelectItem>
              <SelectItem value="licenses">{ORIGIN_LABELS.licenses}</SelectItem>
              <SelectItem value="contact">{ORIGIN_LABELS.contact}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {leadsLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : !filteredLeads || filteredLeads.length === 0 ? (
          <div className="rounded-xl border border-border bg-card flex items-center justify-center py-12">
            <p className="text-sm text-muted-foreground">
              {leads && leads.length > 0 ? 'No hay prospectos con este origen.' : 'Aún no hay prospectos registrados.'}
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">{filteredLeads.length} prospecto{filteredLeads.length !== 1 ? 's' : ''}</p>
            <div className="rounded-xl border border-border bg-card overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Origen</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead>
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        Empleados
                      </span>
                    </TableHead>
                    <TableHead>¿Qué buscan?</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell>
                        <div className="font-medium">{lead.full_name}</div>
                        <div className="text-xs text-muted-foreground">{lead.position}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-sm">
                          <Building2 className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          {lead.company_name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={leadOrigin(lead.looking_for) === 'contact' ? 'outline' : 'secondary'}
                          className="text-xs whitespace-nowrap"
                        >
                          {ORIGIN_LABELS[leadOrigin(lead.looking_for)]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{lead.email}</div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Phone className="w-3 h-3" />
                          {lead.phone}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs whitespace-nowrap">
                          {lead.employee_count}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[220px]">
                        <p className="text-sm text-muted-foreground line-clamp-2">{lead.looking_for}</p>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {format(new Date(lead.created_at), 'dd MMM yyyy, HH:mm', { locale: es })}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => setDeleteId(lead.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este prospecto?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
